export function cn(...parts: unknown[]): string {
  return parts.filter((p): p is string => typeof p === 'string' && p.length > 0).join(' ');
}

const LOCALE = 'tr-TR';

const toNumber = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export function formatNumber(v: number | string | null | undefined, digits = 2): string {
  const n = toNumber(v);
  return n === null ? '—' : n.toLocaleString(LOCALE, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function formatInt(v: number | string | null | undefined): string {
  const n = toNumber(v);
  return n === null ? '—' : Math.round(n).toLocaleString(LOCALE);
}

export function formatMoney(v: number | string | null | undefined, currency = 'TRY', digits = 2): string {
  const n = toNumber(v);
  if (n === null) return '—';
  try {
    return n.toLocaleString(LOCALE, { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits });
  } catch {
    return `${formatNumber(n, digits)} ${currency}`;
  }
}

/** 0.1234 → "%12,34" değil; değer zaten yüzde ise ratio=false. */
export function formatPercent(v: number | string | null | undefined, { ratio = false, digits = 2, sign = false } = {}): string {
  const n0 = toNumber(v);
  if (n0 === null) return '—';
  const n = ratio ? n0 * 100 : n0;
  const s = `%${Math.abs(n).toLocaleString(LOCALE, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
  if (n < 0) return `-${s}`;
  return sign && n > 0 ? `+${s}` : s;
}

export function formatDate(v: string | number | Date | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatDateTime(v: string | number | Date | null | undefined, withSeconds = false): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds && { second: '2-digit' }),
  });
}

export function formatTime(v: string | number | Date | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

const RTF = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });

export function formatRelative(v: string | number | Date | null | undefined, now = Date.now()): string {
  if (!v) return '—';
  const t = new Date(v).getTime();
  if (Number.isNaN(t)) return '—';
  const sec = Math.round((t - now) / 1000);
  const abs = Math.abs(sec);
  if (abs < 60) return RTF.format(sec, 'second');
  if (abs < 3600) return RTF.format(Math.round(sec / 60), 'minute');
  if (abs < 86400) return RTF.format(Math.round(sec / 3600), 'hour');
  if (abs < 86400 * 30) return RTF.format(Math.round(sec / 86400), 'day');
  return formatDate(v);
}

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toLocaleString(LOCALE, { maximumFractionDigits: 1 })} KB`;
  return `${(n / 1024 / 1024).toLocaleString(LOCALE, { maximumFractionDigits: 1 })} MB`;
}

/** Pozitif yeşil, negatif kırmızı metin sınıfı. */
export function signClass(v: number | string | null | undefined): string {
  const n = toNumber(v);
  if (n === null || n === 0) return '';
  return n > 0 ? 'text-success' : 'text-danger';
}
